"use client";

import { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Button } from "@/components/atoms/buttons";
import { __ } from "@/lib/i18n/functions";
import { Locales } from "@/lib/i18n";
import { sdk } from "@/lib/medusa/client";
import type { StoreOrder } from "@medusajs/types";
import OrderLoader from "@/components/modules/account/order-loader";
import OrderTableBody from "@/components/modules/account/order-table-body";

import { Loader2 } from "lucide-react";
import OrderBodyMobile from "./order-body-mobile";

export type OrderTableProps = {
  locale: Locales;
};

const PAGE_LIMIT = 10;

export default function MyOrdersList({ locale }: OrderTableProps) {
  const [orders, setOrders] = useState<StoreOrder[]>([]);
  const [offset, setOffset] = useState<number>(0);
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchOrders = async (offsetValue: number) => {
    setIsLoading(true);
    try {
      const { orders, count } = await sdk.store.order.list({
        limit: PAGE_LIMIT,
        offset: offsetValue,
        order: "-created_at",
      });

      setOrders(orders);
      setCount(count);
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrev = () => {
    if (offset >= PAGE_LIMIT) {
      setOffset(offset - PAGE_LIMIT);
    }
  };

  const handleNext = () => {
    if (offset + PAGE_LIMIT < count) {
      setOffset(offset + PAGE_LIMIT);
    }
  };

  const totalPages = Math.ceil(count / PAGE_LIMIT);
  const currentPage = Math.floor(offset / PAGE_LIMIT) + 1;

  useEffect(() => {
    fetchOrders(offset);
  }, [offset]);

  return (
    <>
      <ScrollArea className={cn("hidden h-fit max-w-full sm:block")}>
        <Table>
          <TableHeader>
            <TableRow className="border-primary-light/30 border-b">
              <TableHead className="text-dark-primary px-4 py-0 font-medium">{__("ORDER", locale)}</TableHead>
              {/* <TableHead className="text-dark-primary px-4 py-0 font-medium">{__("REF", locale)}</TableHead> */}
              <TableHead className="text-dark-primary px-4 py-0 font-medium">{__("ORDER DATE", locale)}</TableHead>
              <TableHead className="text-dark-primary px-4 py-0 font-medium">{__("NUMBER OF ITEMS", locale)}</TableHead>
              <TableHead className="text-dark-primary px-4 py-0 font-medium">{__("AMOUNT", locale)}</TableHead>
              <TableHead className="text-dark-primary px-4 py-0 text-right font-medium"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 10 }).map((_, index) => <OrderLoader key={index} />)
            ) : orders?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground py-8 text-center">
                  {__("No orders found.", locale)}
                </TableCell>
              </TableRow>
            ) : (
              orders?.map((order: StoreOrder, index) => (
                <OrderTableBody index={count - offset - index} order={order} locale={locale} key={order.id} />
              ))
            )}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      {orders && orders.length !== 0 && (
        <div className="flex flex-col items-end space-y-1 px-4 py-4 max-sm:hidden">
          <div className="flex items-center space-x-2">
            <Button className="px-3 py-2 text-base" variant="secondary" disabled={offset === 0} onClick={handlePrev}>
              {__("Previous", locale)}
            </Button>
            <p className="text-muted-foreground text-sm">
              {__("Page", locale)} {currentPage} {__("of", locale)} {totalPages}
            </p>
            <Button
              className="px-3 py-2 text-base"
              variant="secondary"
              disabled={offset + PAGE_LIMIT >= count}
              onClick={handleNext}
            >
              {__("Next", locale)}
            </Button>
          </div>
        </div>
      )}

      {/* Mobile Section */}
      <section className={cn("text-dark-primary block space-y-5 sm:hidden")}>
        {isLoading && (
          <div className="flex items-center justify-center">
            {" "}
            <Loader2 className="animate-spin" />
          </div>
        )}
        {orders && orders.length === 0 && !isLoading && <p>{__("No orders found.", locale)}</p>}
        <div className="space-y-5">
          {orders?.map((order: StoreOrder) => (
            <OrderBodyMobile key={`order-${order.id}-mobile`} order={order} locale={locale} />
          ))}
        </div>
        {orders && orders.length !== 0 && (
          <div className="mt-1 flex flex-col items-end">
            <div className="flex items-center space-x-2">
              <Button className="px-3 py-2 text-base" variant="secondary" disabled={offset === 0} onClick={handlePrev}>
                {__("Previous", locale)}
              </Button>
              <p className="text-muted-foreground text-sm">
                {__("Page", locale)} {currentPage} {__("of", locale)} {totalPages}
              </p>
              <Button
                className="px-3 py-2 text-base"
                variant="secondary"
                disabled={offset + PAGE_LIMIT >= count}
                onClick={handleNext}
              >
                {__("Next", locale)}
              </Button>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
